import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subscription, combineLatest} from "rxjs";
import {effectiveRoles, Role, ROLE_PRECEDENCE, User} from "../../model/user";
import {Producer} from "../../model/evaluation/evaluation";
import {AdminService} from "../../services/admin.service";
import {EvaluationsService} from "../../services/evaluations.service";
import {MatTableModule} from "@angular/material/table";
import {MatSelectModule} from "@angular/material/select";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {FormsModule} from "@angular/forms";
import {NgForOf, NgIf} from "@angular/common";

interface UserRow {
  user: User;
  roles: Role[];
  channelId: number | null;
}

/** What the table is ordered by: a column name, or one of the role columns. */
type SortKey = 'email' | 'displayName' | 'production' | Role;

/**
 * Hovering a role column says what it is for.
 *
 * Only where the name does not: `admin` and `owner` explain themselves,
 * `evaluationAdmin` does not — the interesting half of it is everything it
 * deliberately leaves out, and that is exactly what somebody granting it
 * needs to know.
 */
const ROLE_HINTS: { [role in Role]?: string } = {
  evaluationAdmin: 'Runs /evaluation exactly as an admin does — releasing, ruling, '
    + 'confirming, the recordings — and has no access to /admin: no users, no licences, '
    + 'no leagues, no uploads.',
  producer: 'A production company signing off its own broadcasts. Needs a production below.',
};

@Component({
  selector: 'app-admin-users',
  templateUrl: './admin-users.component.html',
  styleUrls: ['./admin-users.component.scss'],
  standalone: true,
  imports: [
    MatTableModule,
    MatSelectModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    FormsModule,
    NgForOf,
    NgIf,
  ]
})
export class AdminUsersComponent implements OnInit, OnDestroy {

  readonly roles = ROLE_PRECEDENCE;

  rows: UserRow[] = [];
  visible: UserRow[] = [];
  producers: Producer[] = [];
  busy = '';

  search = '';
  roleFilter: Role | '' = '';
  sortKey: SortKey = 'email';
  sortDesc = false;

  private channelNames = new Map<number, string>();
  private subscriptions = new Subscription();

  constructor(
    private adminService: AdminService,
    private evaluations: EvaluationsService,
  ) {
  }

  ngOnInit(): void {
    // A producer account is made here, not on sign-up: anyone who signs in with
    // Google lands as an owner, and an admin then adds the producer role and
    // says which production it belongs to. Adding it takes nothing away —
    // running broadcasts and signing off evaluations are not either/or.
    this.subscriptions.add(
      combineLatest([this.adminService.getAllOwners(), this.evaluations.producers()])
        .subscribe(([users, producers]) => {
          this.producers = [...producers].sort((a, b) => a.name.localeCompare(b.name));
          this.channelNames = new Map(producers.map(producer => [producer.channelId, producer.name]));
          // Rebuilt from scratch on every emission, so an edit in flight is
          // dropped rather than silently reconciled against a newer document.
          this.rows = users.map(user => ({
            user,
            roles: [...effectiveRoles(user)],
            channelId: user.channelId ?? null,
          }));
          this.apply();
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Filter, then sort. Both exist for one job: the producer-to-channel
   * assignment gets screenshotted for the production companies to confirm,
   * and the twenty operator accounts that have nothing to do with it only
   * make the picture harder to read.
   */
  apply(): void {
    const needle = this.search.trim().toLowerCase();
    this.visible = this.rows
      .filter(row => !this.roleFilter || row.roles.includes(this.roleFilter))
      .filter(row => !needle
        || (row.user.email ?? '').toLowerCase().includes(needle)
        || (row.user.displayName ?? '').toLowerCase().includes(needle)
        || this.productionName(row).toLowerCase().includes(needle))
      .sort((a, b) => this.compare(a, b) || this.text(a, 'email').localeCompare(this.text(b, 'email')));
  }

  sortBy(key: SortKey): void {
    // A second click on the same header reverses it; a new column starts the
    // way that column is useful — names A–Z, role and production holders first.
    this.sortDesc = this.sortKey === key ? !this.sortDesc : this.isRoleKey(key) || key === 'production';
    this.sortKey = key;
    this.apply();
  }

  caret(key: SortKey): string {
    return this.sortKey !== key ? '' : this.sortDesc ? '▼' : '▲';
  }

  /** The production a row is assigned to, empty when it has none. */
  productionName(row: UserRow): string {
    return row.channelId === null ? '' : this.channelNames.get(row.channelId) ?? `#${row.channelId}`;
  }

  /** What a role column means, where the name alone does not say it. */
  roleHint(role: Role): string {
    return ROLE_HINTS[role] ?? '';
  }

  has(row: UserRow, role: Role): boolean {
    return row.roles.includes(role);
  }

  toggle(row: UserRow, role: Role, checked: boolean): void {
    row.roles = checked
      ? [...row.roles, role]
      : row.roles.filter(held => held !== role);
    if (!this.has(row, 'producer')) {
      row.channelId = null;
    }
  }

  async save(row: UserRow): Promise<void> {
    this.busy = row.user.uid;
    try {
      await this.adminService.setRoles(row.user.uid, row.roles, row.channelId);
    } finally {
      this.busy = '';
    }
  }

  changed(row: UserRow): boolean {
    const before = [...effectiveRoles(row.user)].sort().join(',');
    const now = [...row.roles].sort().join(',');
    return before !== now
      || (this.has(row, 'producer') && row.channelId !== (row.user.channelId ?? null));
  }

  /** A producer without a channel sees nothing, so say so rather than save quietly. */
  incomplete(row: UserRow): boolean {
    return this.has(row, 'producer') && !row.channelId;
  }

  private isRoleKey(key: SortKey): key is Role {
    return (ROLE_PRECEDENCE as string[]).includes(key);
  }

  private compare(a: UserRow, b: UserRow): number {
    const direction = this.sortDesc ? -1 : 1;
    if (this.isRoleKey(this.sortKey)) {
      return direction * (Number(this.has(a, this.sortKey)) - Number(this.has(b, this.sortKey)));
    }
    // An unassigned production sorts last either way: "no answer yet" is not
    // the same as an answer and does not belong among the named ones.
    if (this.sortKey === 'production') {
      const left = this.productionName(a);
      const right = this.productionName(b);
      if (!left || !right) {
        return Number(!left) - Number(!right);
      }
      return direction * left.localeCompare(right);
    }
    return direction * this.text(a, this.sortKey).localeCompare(this.text(b, this.sortKey));
  }

  private text(row: UserRow, key: 'email' | 'displayName'): string {
    return (key === 'email' ? row.user.email : row.user.displayName) ?? '';
  }
}
